// Feedback Dashboard (Reports > Dashboards > Feedback) — customer-feedback
// KPIs, proxied from the standalone MF-Customer-Feedback-App-Api service via
// apparel-api's getFeedbackSummary resolver (see FeedbackService.ts). Follows
// the same combined-GraphQL-query pattern as every other dashboard screen in
// this folder (SalesDashboardScreen.tsx, CustomerCreditDashboardScreen.tsx…).
//
// Field names here match the feedback service's service-token /feedback/summary
// route (NOT /reports/summary, which is JWT-only) exactly: total,
// averageOverall, averages{8 keys}, channelBreakdown{store,online,exhibition},
// ratingDistribution{"1".."5"}, filtered by storeLocationId/purchaseChannel.
// There's no per-store breakdown or trend-over-time in that response, so this
// screen doesn't attempt either.
//
// apparel-api reaches that service with a static shared service key
// (server-to-server) — no user login is involved anywhere in this path.
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { MessageSquareText, Star } from "lucide-react";
import { getFeedbackDashboardDataAction, getFeedbackLocationsAction } from "../../../Store/actions/apparelAction";
import ApparelMenu from "../../SubComponents/apparelMenu";
import { StatCard } from "../../../shared/stat-card";
import { Skeleton } from "ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "ui/select";
import { ChartCard } from "./ChartCard";
import { CategoryBarChart } from "./CategoryBarChart";
import { CategoryDonutChart } from "./CategoryDonutChart";
import { PURCHASE_CHANNELS, RATING_DIMENSIONS, channelLabel, FeedbackKioskLocation, locationLabel } from "../../Feedback/feedbackConstants";

const num = (v: any) => new Intl.NumberFormat("en-IN").format(Number(v) || 0);

const ALL = "all";
const OVERALL_RATING_KEYS = ["1", "2", "3", "4", "5"];

const FeedbackDashboardScreen = () => {
  const dispatch = useDispatch();
  const apparelReducerState: any = useSelector((state: any) => state.ApparelReducerState);
  const { getFeedbackDashboardData, getFeedbackDashboardDataLoad, getFeedbackLocations } = apparelReducerState;

  const [storeLocationId, setStoreLocationId] = React.useState<string>("");
  const [purchaseChannel, setPurchaseChannel] = React.useState<string>("");

  const fetchDashboard = React.useCallback(
    (store?: string, channel?: string) => {
      dispatch(
        getFeedbackDashboardDataAction({
          storeLocationId: store || "",
          purchaseChannel: channel || "",
        })
      );
    },
    [dispatch]
  );

  React.useEffect(() => {
    fetchDashboard(storeLocationId, purchaseChannel);
    dispatch(getFeedbackLocationsAction());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onStoreChange = (value: string) => {
    const next = value === ALL ? "" : value;
    setStoreLocationId(next);
    fetchDashboard(next, purchaseChannel);
  };

  const onChannelChange = (value: string) => {
    const next = value === ALL ? "" : value;
    setPurchaseChannel(next);
    fetchDashboard(storeLocationId, next);
  };

  const summary = getFeedbackDashboardData?.getFeedbackSummary;
  const loading = getFeedbackDashboardDataLoad && !summary;
  const locations: FeedbackKioskLocation[] = (getFeedbackLocations?.getFeedbackLocations?.items || []).filter(
    (l: FeedbackKioskLocation) => l.isActive
  );

  const ratingByCategory = RATING_DIMENSIONS.map((d) => ({
    label: d.label,
    value: Number(summary?.averages?.[d.key]) || 0,
  }));

  const channelBreakdown = Object.entries(summary?.channelBreakdown || {}).map(([id, count]) => ({
    category: channelLabel(id),
    amount: Number(count) || 0,
  }));

  // Histogram of the overall-experience star rating (1..5) across submissions
  // in the current filter — distinct from averages.overallExperience, which
  // is the mean, not the distribution.
  const ratingDistribution = OVERALL_RATING_KEYS.map((key) => ({
    label: key,
    value: Number(summary?.ratingDistribution?.[key]) || 0,
  }));

  return (
    <div className="mz h-full overflow-y-auto px-6 py-4">
      <ApparelMenu selectedKey="feedback-dashboard" />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <Select value={storeLocationId || ALL} onValueChange={onStoreChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Stores" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Stores</SelectItem>
            {locations.map((s) => (
              <SelectItem key={s.id} value={s.id}>{locationLabel(s)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={purchaseChannel || ALL} onValueChange={onChannelChange}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="All Channels" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Channels</SelectItem>
            {PURCHASE_CHANNELS.map((c) => (
              <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* KPI cards */}
      <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-2 lg:grid-cols-2">
        {loading ? (
          Array.from({ length: 2 }).map((_, i) => <Skeleton key={i} className="h-24 w-full rounded-xl" />)
        ) : (
          <>
            <StatCard label="Total Feedback" value={num(summary?.total)} icon={MessageSquareText} />
            <StatCard label="Avg. Overall" value={`${(Number(summary?.averageOverall) || 0).toFixed(1)}/5`} icon={Star} />
          </>
        )}
      </div>

      {/* Charts */}
      <div className="mb-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <ChartCard title="Average rating by category" loading={loading} isEmpty={!ratingByCategory.some((d) => d.value)} className="lg:col-span-2">
          <CategoryBarChart data={ratingByCategory} valueFormatter={(v) => v.toFixed(1)} />
        </ChartCard>
        <ChartCard title="Purchase channel breakdown" loading={loading} isEmpty={!channelBreakdown.some((d) => d.amount)}>
          <CategoryDonutChart data={channelBreakdown} />
        </ChartCard>
        <ChartCard title="Overall rating distribution" loading={loading} isEmpty={!ratingDistribution.some((d) => d.value)}>
          <CategoryBarChart data={ratingDistribution} />
        </ChartCard>
      </div>
    </div>
  );
};

export default FeedbackDashboardScreen;
